import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { PromocodeService } from '../../../../../services/promo-code';

@Component({
  selector: 'app-promocode',
  templateUrl: './promocode.component.html',
  styleUrl: './promocode.component.scss'
})
export class PromocodeComponent implements OnInit {
  allData: any[] = [];
  displayedColumns: string[] = ["code", "type", "Percentage", "expirationDate", "active", "actions"];
  dataSource: MatTableDataSource<any>;

  contentReady: boolean = true;
  noData: boolean = false;
  isAr: boolean = localStorage.getItem("language") === "ar";

  promocodeForm!: FormGroup;
  submitted = false;

  showExpirationCalendar: boolean = false;
  expirationCalendarViewDate: Date = new Date();
  todayDateOnly: string = this.formatDateForInput(new Date());

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private promoService: PromocodeService,
    private fb: FormBuilder
  ) {
    this.dataSource = new MatTableDataSource(this.allData);
  }

  ngOnInit(): void {
    this.initForm();
    this.loadPromocodes();
  }

  initForm(): void {
    this.promocodeForm = this.fb.group({
      code: ['', [Validators.required, Validators.minLength(2), Validators.pattern(/^[A-Za-z0-9_-]+$/)]],
      type: ['1', Validators.required],
      percentage: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
      expiration_date: ['', this.futureDateValidator],
      active: [true]
    });

    this.promocodeForm.get('type')?.valueChanges.subscribe((type) => {
      this.updatePercentageValidators(type);
    });

    this.syncExpirationCalendarView();
  }

  private updatePercentageValidators(type: any): void {
    const percentageControl = this.promocodeForm.get('percentage');
    if (!percentageControl) return;

    if (String(type) === '1') {
      percentageControl.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
    } else {
      percentageControl.clearValidators();
      percentageControl.setValue('');
    }

    percentageControl.updateValueAndValidity();
  }

  futureDateValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;

    const today = this.parseDateOnly(this.todayDateOnly) || new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = this.parseDateOnly(control.value);
    if (!selectedDate) return { invalidDate: true };

    selectedDate.setHours(0, 0, 0, 0);

    return selectedDate < today ? { pastDate: true } : null;
  };

  shouldShowError(controlName: string): boolean {
    const control = this.promocodeForm?.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  getErrorMessage(controlName: string, label: string): string {
    const control = this.promocodeForm?.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.isAr ? `${label} مطلوب` : `${label} is required`;
    }

    if (control.errors['minlength']) {
      const requiredLength = control.errors['minlength'].requiredLength;
      return this.isAr
        ? `${label} لازم يكون ${requiredLength} حروف على الأقل`
        : `${label} must be at least ${requiredLength} characters`;
    }

    if (control.errors['pattern']) {
      return this.isAr
        ? `${label} لازم يكون حروف أو أرقام بدون مسافات`
        : `${label} must use letters or numbers without spaces`;
    }

    if (control.errors['min'] || control.errors['max']) {
      return this.isAr
        ? `${label} لازم يكون بين 1 و 100`
        : `${label} must be between 1 and 100`;
    }

    if (control.errors['pastDate']) {
      return this.isAr
        ? `${label} لازم يكون اليوم أو تاريخ قادم`
        : `${label} must be today or a future date`;
    }

    if (control.errors['invalidDate']) {
      return this.isAr
        ? `${label} لازم يكون تاريخ صحيح`
        : `${label} must be a valid date`;
    }

    return this.isAr ? `${label} غير صحيح` : `${label} is invalid`;
  }

  getMissingRequiredCount(): number {
    if (!this.promocodeForm) {
      return 0;
    }

    return Object.keys(this.promocodeForm.controls).filter((controlName) => {
      const control = this.promocodeForm.get(controlName);
      return control?.hasError('required');
    }).length;
  }

  private validateBeforeSubmit(): boolean {
    this.submitted = true;

    if (this.promocodeForm.invalid) {
      this.promocodeForm.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'تأكد من بيانات كود الخصم' : 'Please check promo code data',
        text: this.isAr
          ? 'راجع الرسائل تحت الحقول المطلوبة أو غير الصحيحة'
          : 'Review the messages below the required or invalid fields',
        confirmButtonText: this.isAr ? 'تمام' : 'OK',
      });

      return false;
    }

    return true;
  }

  loadPromocodes(): void {
    this.contentReady = true;

    this.promoService.getAll().subscribe({
      next: (data) => {
        this.allData = data || [];
        this.dataSource = new MatTableDataSource(this.allData);
        this.dataSource.paginator = this.paginator;
        this.contentReady = false;
        this.noData = this.allData.length === 0;
      },
      error: (err) => {
        console.error(err);
        this.contentReady = false;
        this.noData = true;
      },
    });
  }

  addCode(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    this.promoService.create(this.promocodeForm.value).subscribe({
      next: () => {
        this.loadPromocodes();

        this.promocodeForm.reset({
          code: '',
          type: '1',
          percentage: '',
          expiration_date: '',
          active: true
        });

        this.submitted = false;
        this.closeExpirationCalendar();
        this.syncExpirationCalendarView();

        Swal.fire({
          icon: 'success',
          title: this.isAr ? 'تمت الإضافة' : 'Added successfully',
          confirmButtonText: this.isAr ? 'تمام' : 'OK',
        });
      },
      error: (err) => {
        console.error(err);

        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'خطأ' : 'Error',
          text: this.isAr ? 'تعذر إضافة كود الخصم' : 'Failed to add promo code',
          confirmButtonText: this.isAr ? 'تمام' : 'OK',
        });
      },
    });
  }

  deleteItem(id: number): void {
    Swal.fire({
      title: this.isAr ? 'هل أنت متأكد؟' : 'Are you sure?',
      text: this.isAr ? 'لن يمكنك التراجع بعد الحذف!' : "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: this.isAr ? 'نعم، احذف!' : 'Yes, delete it!',
      cancelButtonText: this.isAr ? 'إلغاء' : 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.promoService.delete(id).subscribe({
          next: () => {
            this.loadPromocodes();

            Swal.fire({
              icon: 'success',
              title: this.isAr ? 'تم الحذف!' : 'Deleted!',
              text: this.isAr ? 'تم حذف كود الخصم بنجاح.' : 'Promo code has been deleted.',
              confirmButtonText: this.isAr ? 'تمام' : 'OK',
            });
          },
          error: (err) => {
            console.error(err);

            Swal.fire({
              icon: 'error',
              title: this.isAr ? 'خطأ' : 'Error',
              text: this.isAr ? 'تعذر حذف كود الخصم' : 'Failed to delete promo code',
              confirmButtonText: this.isAr ? 'تمام' : 'OK',
            });
          },
        });
      }
    });
  }

  /* ================================
     Expiration Calendar
     نفس Calendar بتاع New Booking
     ================================ */

  toggleExpirationCalendar(): void {
    this.showExpirationCalendar = !this.showExpirationCalendar;

    if (this.showExpirationCalendar) {
      const selectedDate = this.parseDateOnly(this.promocodeForm.get('expiration_date')?.value);
      const baseDate = selectedDate || this.parseDateOnly(this.todayDateOnly) || new Date();

      this.expirationCalendarViewDate = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1
      );
    }
  }

  closeExpirationCalendar(): void {
    this.showExpirationCalendar = false;
  }

  previousExpirationCalendarMonth(): void {
    this.expirationCalendarViewDate = new Date(
      this.expirationCalendarViewDate.getFullYear(),
      this.expirationCalendarViewDate.getMonth() - 1,
      1
    );
  }

  nextExpirationCalendarMonth(): void {
    this.expirationCalendarViewDate = new Date(
      this.expirationCalendarViewDate.getFullYear(),
      this.expirationCalendarViewDate.getMonth() + 1,
      1
    );
  }

  getExpirationCalendarTitle(): string {
    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      month: 'long',
      year: 'numeric',
    }).format(this.expirationCalendarViewDate);
  }

  getExpirationCalendarWeekDays(): string[] {
    return this.isAr
      ? ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  getExpirationCalendarDays(): Array<{
    date: Date;
    dayNumber: string;
    isCurrentMonth: boolean;
    isToday: boolean;
    isSelected: boolean;
    isDisabled: boolean;
  }> {
    const year = this.expirationCalendarViewDate.getFullYear();
    const month = this.expirationCalendarViewDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstDay.getDay());

    const today = this.parseDateOnly(this.todayDateOnly) || new Date();
    today.setHours(0, 0, 0, 0);

    const selectedDate = this.parseDateOnly(this.promocodeForm.get('expiration_date')?.value);

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      date.setHours(0, 0, 0, 0);

      return {
        date,
        dayNumber: this.isAr ? this.toArabicDigits(date.getDate()) : String(date.getDate()),
        isCurrentMonth: date.getMonth() === month,
        isToday: this.isSameDate(date, today),
        isSelected: selectedDate ? this.isSameDate(date, selectedDate) : false,

        // نفس شرط الـ futureDateValidator: ممنوع تاريخ قبل النهارده
        isDisabled: date < today
      };
    });
  }

  selectExpirationCalendarDate(date: Date): void {
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    const today = this.parseDateOnly(this.todayDateOnly) || new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) return;

    this.promocodeForm.patchValue({
      expiration_date: this.formatDateForInput(selectedDate)
    });

    this.promocodeForm.get('expiration_date')?.markAsTouched();
    this.promocodeForm.get('expiration_date')?.updateValueAndValidity();

    this.closeExpirationCalendar();
  }

  getExpirationDateDisplay(): string {
    const date = this.parseDateOnly(this.promocodeForm?.get('expiration_date')?.value);

    if (!date) {
      return this.isAr ? 'اختار تاريخ الانتهاء' : 'Select expiration date';
    }

    return new Intl.DateTimeFormat(this.isAr ? 'ar-EG' : 'en-US', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  }

  private syncExpirationCalendarView(): void {
    const selectedDate = this.parseDateOnly(this.promocodeForm?.get('expiration_date')?.value);
    const baseDate = selectedDate || this.parseDateOnly(this.todayDateOnly) || new Date();

    this.expirationCalendarViewDate = new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      1
    );
  }

  private parseDateOnly(value: any): Date | null {
    if (!value) return null;

    const cleanValue = String(value)
      .replace('T', ' ')
      .replace('Z', '')
      .split('.')[0]
      .trim()
      .split(' ')[0];

    const match = cleanValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (!match) return null;

    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }

  private isSameDate(first: Date, second: Date): boolean {
    return first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
  }

  private formatDateForInput(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');
  }

  private toArabicDigits(value: string | number): string {
    return String(value).replace(/\d/g, (digit) => '٠١٢٣٤٥٦٧٨٩'[Number(digit)]);
  }
}